'use client';

import { useMemo, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useTranslations } from 'next-intl';
import type { AreaResponse } from '@/src/lib/api';
import type { DisplaySearchPromptPostInquiryFilter } from '@/src/lib/api';
import { useFeatureFlag } from '@/src/lib/feature-flags';
import {
  useCurrentArea,
  useCurrentUser,
  useDisplaySearchPromptPostInquiry,
  useFreepassLogin,
  useInquiryMessageCache,
  userListingKeys,
  useSubmitInquiry,
  useValidateContactInfo,
} from '@/src/lib/hooks/query';
import { inquiryKeys } from '@/src/lib/hooks/query/queryKeys';
import { useOptionalFilters } from '@/src/lib/filters';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { getActiveSearchHdpBackPath } from '@/src/lib/navigation/searchHdpHistory';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useSimilarListings } from '@/src/components/features/property/hooks/useSimilarListings';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import { analytics } from '@/src/components/features/property/lib';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import {
  emitTrackDemotedListingLeadEvent,
  emitTrackFreeListingLeadEvent,
  emitTrackPaidListingLeadEvent,
} from '@/src/analytics/hotpads/events';
import { hasSeenRenterProfilePrompt, markRenterProfilePromptShown } from './renterProfilePromptTracking';
import type {
  ContactFormHostMode,
  ContactFormInputValues,
  ContactFormMode,
  ContactFormSavedSearchContext,
  ContactFormServerErrors,
  ContactSubmissionContext,
} from './types';

type UseHdpContactSubmissionOptions = {
  listing?: PropertyDetailListing | null;
  mode: ContactFormMode;
  formMode: ContactFormHostMode;
};

type ParsedSearchPath = {
  resourceId: string;
  searchSlug: string;
};

const DEFAULT_POST_CONTACT_SEARCH_SLUG = 'apartments-for-rent';

const DEFAULT_SERVER_ERRORS: ContactFormServerErrors = {
  emailError: '',
  phoneError: '',
  formError: '',
};

function formatPhoneForInquiry(raw: string) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function createRentalSubmitId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseSearchPath(searchPath: string | null): ParsedSearchPath | null {
  const path = searchPath?.split(/[?#]/)[0];
  const segments = path?.split('/').filter(Boolean) ?? [];
  if (segments.length < 2) return null;

  const [resourceId, searchSlug] = segments;
  if (!resourceId || !searchSlug) return null;
  if (searchSlug === 'pad' || searchSlug === 'building') return null;

  return { resourceId, searchSlug };
}

function hasUsableBounds(area: AreaResponse | undefined): area is AreaResponse & {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  return (
    typeof area?.minLat === 'number' &&
    typeof area.maxLat === 'number' &&
    typeof area.minLon === 'number' &&
    typeof area.maxLon === 'number'
  );
}

function getSavedSearchContext(
  area: AreaResponse | undefined,
  parsedSearchPath: ParsedSearchPath | null,
  filters: FilterState,
): ContactFormSavedSearchContext | null {
  const areaId = area?.id ?? area?.resourceId;
  const locationText = area?.name ?? area?.city ?? area?.resourceId;
  const searchPath =
    parsedSearchPath ??
    (area?.resourceId
      ? {
          resourceId: area.resourceId,
          searchSlug: DEFAULT_POST_CONTACT_SEARCH_SLUG,
        }
      : null);

  if (!areaId || !locationText || !searchPath || !hasUsableBounds(area)) {
    return null;
  }

  return {
    areaId,
    resourceId: searchPath.resourceId,
    searchSlug: searchPath.searchSlug,
    locationText,
    areaBounds: {
      minLat: area.minLat,
      maxLat: area.maxLat,
      minLon: area.minLon,
      maxLon: area.maxLon,
    },
    filters,
  };
}

function buildDisplaySearchPromptFilter(
  savedSearchContext: ContactFormSavedSearchContext | null,
): DisplaySearchPromptPostInquiryFilter | null {
  if (!savedSearchContext) return null;

  return {
    areas: savedSearchContext.areaId,
    minLat: savedSearchContext.areaBounds.minLat,
    maxLat: savedSearchContext.areaBounds.maxLat,
    minLon: savedSearchContext.areaBounds.minLon,
    maxLon: savedSearchContext.areaBounds.maxLon,
  };
}

function getAvailableSimilarListings(
  similarListings: Array<SimilarListing> | undefined,
  listing: PropertyDetailListing,
) {
  const listingAlias = listing.aliasEncoded;
  const listingMaloneLotId = listing.maloneLotIdEncoded;

  return (similarListings ?? [])
    .filter((similarListing) => {
      const aliasMatches = Boolean(listingAlias) && similarListing.aliasEncoded === listingAlias;
      const lotMatches =
        Boolean(listingMaloneLotId) &&
        (similarListing.maloneLotIdEncoded === listingMaloneLotId ||
          similarListing.lotIdEncoded === listingMaloneLotId);

      return Boolean(similarListing.aliasEncoded) && !aliasMatches && !lotMatches;
    })
    .slice(0, 5);
}

function isDemotedLead(listing: PropertyDetailListing): boolean {
  return Boolean(
    (listing as { isDemoted?: boolean | null }).isDemoted ||
    (listing as { details?: { demoted?: boolean | null } }).details?.demoted,
  );
}

function isPaidLead(listing: PropertyDetailListing): boolean {
  return Boolean(
    (listing as { isPaidAndNotDemoted?: boolean | null }).isPaidAndNotDemoted ||
    (listing as { isPaidMultifamily?: boolean | null }).isPaidMultifamily,
  );
}

function getInquiryText(values: ContactFormInputValues) {
  return [values.userMessage, values.lowIncomeMessage]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('\n\n');
}

export function useHdpContactSubmission({ listing, mode, formMode }: UseHdpContactSubmissionOptions) {
  const t = useTranslations('contactForm');
  const queryClient = useQueryClient();
  const filterContext = useOptionalFilters();
  const filters = filterContext?.filters ?? DEFAULT_FILTERS;
  const mockInquiryEnabled = useFeatureFlag('mock-inquiry');
  const { data: currentUser } = useCurrentUser();
  const { data: currentArea } = useCurrentArea();
  const { data: similarListings } = useSimilarListings({
    maloneLotIdEncoded: listing?.maloneLotIdEncoded,
    unit: listing?.unit,
  });
  const { data: inquiryMessageCache } = useInquiryMessageCache();
  const validateContactInfo = useValidateContactInfo();
  const submitInquiry = useSubmitInquiry();
  const freepassLogin = useFreepassLogin();
  const [serverErrors, setServerErrors] = useState<ContactFormServerErrors>(DEFAULT_SERVER_ERRORS);

  const savedSearchContext = useMemo(() => {
    const parsedSearchPath = parseSearchPath(getActiveSearchHdpBackPath());
    return getSavedSearchContext(currentArea, parsedSearchPath, filters);
  }, [currentArea, filters]);

  const displaySearchPromptFilter = useMemo(
    () => buildDisplaySearchPromptFilter(savedSearchContext),
    [savedSearchContext],
  );

  const displaySearchPromptPostInquiry = useDisplaySearchPromptPostInquiry(displaySearchPromptFilter, {
    enabled: Boolean(displaySearchPromptFilter) && !mockInquiryEnabled,
  });

  const isLoading =
    !mockInquiryEnabled &&
    (currentUser === undefined || validateContactInfo.isPending || submitInquiry.isPending || freepassLogin.isPending);

  const submitContact = async (values: ContactFormInputValues): Promise<ContactSubmissionContext | null> => {
    setServerErrors(DEFAULT_SERVER_ERRORS);

    if (!mockInquiryEnabled && currentUser === undefined) return null;

    if (!listing?.aliasEncoded) {
      setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
      return null;
    }

    const rentalSubmitId = createRentalSubmitId();
    const text = getInquiryText(values);
    const formattedPhone = formatPhoneForInquiry(values.userPhoneNumber);
    const triggerObject =
      formMode === 'inline' ? 'home_details_component|inline_form' : 'home_details_component|modal_contact_form';

    try {
      let userExists = false;
      let isLoggedIn = Boolean(currentUser?.loggedIn);

      if (!mockInquiryEnabled) {
        const validation = await validateContactInfo.mutateAsync({
          listingAlias: listing.aliasEncoded,
          email: values.userEmail,
          phone: values.userPhoneNumber,
        });

        if (validation.data?.email?.valid === false || validation.data?.phone?.valid === false) {
          setServerErrors({
            emailError: validation.data.email?.valid === false ? t('errorContactValidation') : '',
            phoneError: validation.data.phone?.valid === false ? t('errorContactValidation') : '',
            formError: '',
          });
          return null;
        }
      }

      // HP-8576 legacy parity: submit events must fire before freepass login and the inquiry POST
      analytics.emitTrackContactClickEvent({
        triggerLocation: 'home_details_halo',
        triggerObject,
        listing,
        mode,
        phase: 'submit',
        rentalSubmitId,
      });

      const leadEventPayload = {
        listing,
        triggerLocation: 'home_details',
        triggerObject,
        triggerSource:
          mode === 'requestToApply' ? 'button_to_complete_request_to_apply_form' : 'button_to_complete_contact_form',
      };

      if (isDemotedLead(listing)) {
        emitTrackDemotedListingLeadEvent(leadEventPayload);
      } else if (isPaidLead(listing)) {
        emitTrackPaidListingLeadEvent(leadEventPayload);
      } else {
        emitTrackFreeListingLeadEvent(leadEventPayload);
      }

      if (!mockInquiryEnabled && !isLoggedIn) {
        const authResponse = await freepassLogin.mutateAsync({
          email: values.userEmail,
          url: typeof window === 'undefined' ? undefined : window.location.href,
        });

        if (authResponse.status === 'ACCOUNT_DEACTIVATED') {
          setServerErrors((prev) => ({ ...prev, emailError: t('errorAccountDeactivated') }));
          return null;
        }

        userExists = authResponse.status === 'USER_ALREADY_EXISTS' && !authResponse.loggedIn;
        if (authResponse.success === false && !userExists) {
          setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
          return null;
        }

        isLoggedIn = Boolean(authResponse.loggedIn);
      }

      if (!mockInquiryEnabled) {
        const response = await submitInquiry.mutateAsync({
          alias: listing.aliasEncoded,
          beds: values.numBeds || null,
          baths: values.numBaths || null,
          email: values.userEmail,
          phone: formattedPhone,
          name: values.userName,
          text,
          emailUser: true,
          modelId: null,
          requestToApply: mode === 'requestToApply',
          rentalSubmitId,
          ...(values.moveDate ? { checkInDate: values.moveDate } : {}),
        });

        if (response.success === false || response.error || response.cachedRequest) {
          setServerErrors((prev) => ({
            ...prev,
            formError: response.message || t('errorSubmit'),
          }));
          return null;
        }
      }

      queryClient.setQueryData(inquiryKeys.messageCache(), {
        email: values.userEmail,
        name: values.userName,
        phone: formattedPhone,
        text: values.userMessage,
      });
      queryClient.invalidateQueries({ queryKey: userListingKeys.all });

      const promptResult = savedSearchContext
        ? mockInquiryEnabled
          ? true
          : isLoggedIn
            ? (displaySearchPromptPostInquiry.data ??
              (await displaySearchPromptPostInquiry.refetch().catch(() => ({ data: false }))).data ??
              false)
            : false
        : false;

      const shouldShowRenterProfile = isLoggedIn && !hasSeenRenterProfilePrompt();
      if (shouldShowRenterProfile) {
        markRenterProfilePromptShown();
      }

      return {
        listing,
        mode,
        formMode,
        inquiry: {
          name: values.userName,
          email: values.userEmail,
          phone: formattedPhone,
          text,
        },
        userExists,
        isLoggedIn,
        isWaitlist: isWaitlistedListing(listing),
        shouldShowRenterProfile,
        shouldShowSavedSearch: Boolean(promptResult && savedSearchContext),
        savedSearchContext,
        similarListings: getAvailableSimilarListings(similarListings, listing),
      };
    } catch {
      setServerErrors((prev) => ({
        ...prev,
        formError: t('errorSubmit'),
      }));
      return null;
    }
  };

  return {
    inquiryMessageCache,
    isLoading,
    serverErrors,
    submitContact,
  };
}
